import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Browser from '@wetransfer/concorde-browser';

import Actions from '@app/store/action';
import Assets from '@app/assets';
import I18n from '@module/i18n';
import Router from '@module/router';
import LaunchDarkly from '@module/launchdarkly';
import Spinner from '@app/component/spinner';

import { TRANSFER_STATES } from '@app/transfer-bundle/constant/transfer-states';
import { FF_FORCE_ACCOUNT_VERIFICATION } from '@app/constant/experiments';
import { TRANSFER_GROUP } from '@app/constant/transfer-group';

export function AccountEmailVerification({
  prePopulatedEmail,
  verifyAccountEmail,
  setTransferState,
  route,
  user,
}) {
  const [isVerifying, setIsVerifying] = useState(false);

  function handleResendButtonClick() {
    setTransferState(TRANSFER_STATES.VERIFY_RESEND);
  }

  useEffect(() => {
    async function verifyAccountSignature() {
      const shouldForceVerification =
        LaunchDarkly.getVariant(FF_FORCE_ACCOUNT_VERIFICATION) &&
        user.hasSkippedVerification;

      if (route.query.sig && (!user.isVerified || shouldForceVerification)) {
        try {
          setIsVerifying(true);
          await verifyAccountEmail({
            verificationSignature: route.query.sig,
            email: prePopulatedEmail,
          });

          if (Browser.isMobile) {
            Router.navigateTo('/upload');
            return;
          }

          if (
            LaunchDarkly.getVariant(FF_FORCE_ACCOUNT_VERIFICATION) &&
            route.query.receiving_email
          ) {
            const searchParams = new URLSearchParams();
            searchParams.set('group', TRANSFER_GROUP.RECEIVED);
            searchParams.set('receiving_email', route.query.receiving_email);
            Router.navigateTo('/transfers', true, {
              search: searchParams.toString(),
            });
            return;
          }

          Router.navigateTo('/');
        } catch {
          setIsVerifying(false);
          setTransferState(TRANSFER_STATES.VERIFY_ACCOUNT_FAILED);
        }
      }
    }

    verifyAccountSignature();
  }, [
    route.query.sig,
    user.isVerified,
    prePopulatedEmail,
    verifyAccountEmail,
    setTransferState,
    route.query.receiving_email,
    user.hasSkippedVerification,
  ]);

  return (
    <div className="account-email-verification">
      {isVerifying ? (
        <Spinner
          rotate={true}
          percentage={25}
          hideText
          circumference={80}
          stroke={3}
          className="authentication-code-step__spinner"
        />
      ) : (
        <React.Fragment>
          <img
            className="account-email-verification__lock"
            src={Assets.lookup('transfer_window/email-verification-lock.svg')}
            alt="Lock"
          />
          <h2 className="account-email-verification__title">
            {I18n.t('transfer.email-verification.title')}
          </h2>
          <p
            className="account-email-verification__body"
            dangerouslySetInnerHTML={{
              __html: I18n.t('transfer.email-verification.body_link', {
                account_email: prePopulatedEmail,
              }),
            }}
          />
          <button
            className="account-email-verification__resend-button"
            onClick={handleResendButtonClick}
            type="button"
          >
            {I18n.t('transfer.email-verification.resend')}
          </button>
        </React.Fragment>
      )}
    </div>
  );
}

AccountEmailVerification.propTypes = {
  route: PropTypes.object,
  user: PropTypes.object,
  prePopulatedEmail: PropTypes.string.isRequired,
  verifyAccountEmail: PropTypes.func.isRequired,
  setTransferState: PropTypes.func.isRequired,
};

const mapStateToProps = ({ route, user }) => ({ route, user });

const mapDispatchToProps = {
  verifyAccountEmail: Actions.User.verifyAccountEmail,
  setTransferState: Actions.Transfer.setState,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AccountEmailVerification);
